import React, { useRef, useState } from 'react';
import './Matching.css';
import { detectJobPoolContentType } from '../../services/jobPoolApi.js';
import { useJobUpload } from '../../contexts/JobUploadContext.js';
import { INSUFFICIENT_POINTS_MESSAGE } from './aiQuota';

const MAX_FILE_SIZE_MB = 30;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

/**
 * 求人取込 UI (SCR-M-001 内、T-001-U1)
 *
 * ドロップゾーンから求人ファイル (PDF のみ。CSV は対象外、Issue #1050) を取込む。形式を判定し、
 * 1ファイルごとの状態 (待機中/アップロード中/抽出中/完了/エラー) を表示する
 * (画面レイアウト_SCR-M-001.md §4.1 IN-M-001・§4.2 DP-M-010・§6.2・§6.3 準拠)。
 * 取込そのものと取込状況は JobUploadProvider (ルーティングより上) が持つため、
 * 本コンポーネントが画面から外れても取込は続き、再表示すると状況が見える (Issue #759)。
 * 取込の完了で一覧を取り直す場合は useJobUploadCompleted を使う。
 *
 * @param {{targetCandidateId: string, canImport?: boolean}} props
 *   canImport: AI ポイントの残量が求人票の取込に必要なポイント以上か (§4.1 IN-M-001・§6.4.4、Issue #882)。
 *   false のときはファイルを受け付けず (選択を開かない・ドロップしたファイルは検査も列への追加もせず捨てる)、
 *   再試行 (BTN-M-008) も押せない。判定は呼び出し側 (RecommendationList) が aiQuota.js で行う
 */
function JobUploadZone({ targetCandidateId, canImport = true }) {
    const [isDragActive, setIsDragActive] = useState(false);
    const [zoneError, setZoneError] = useState(null);
    const fileInputRef = useRef(null);
    const { entries: allEntries, enqueueFiles, retryEntry, dismissEntry } = useJobUpload();
    const entries = allEntries.filter((entry) => entry.opportunityId === targetCandidateId);

    const validateFiles = (filesArr) => {
        const validFiles = [];
        const errorMessages = [];

        filesArr.forEach((file) => {
            if (!detectJobPoolContentType(file)) {
                errorMessages.push(`「${file.name}」: PDF ファイルのみ対応しています。`);
                return;
            }
            if (file.size > MAX_FILE_SIZE_BYTES) {
                errorMessages.push(`「${file.name}」: ${MAX_FILE_SIZE_MB}MB を超えるファイルはアップロードできません。`);
                return;
            }
            validFiles.push(file);
        });

        setZoneError(errorMessages.length > 0 ? errorMessages.join('\n') : null);
        return validFiles;
    };

    const handleFiles = (fileList) => {
        // 残量が足りないときはファイルを受け付けない (§4.1 IN-M-001)。ドロップ・ファイル選択の
        // どちらの経路もここを通るため、入口をまとめて塞ぐ (選択は開かないので通常は届かないが、
        // 開いている間に残量が足りなくなる場合がある)
        if (!canImport) return;
        const filesArr = Array.from(fileList);
        if (filesArr.length === 0) return;
        const validFiles = validateFiles(filesArr);
        // 取込の列に加える。取込は 1 件ずつ順に行われる (JobUploadProvider)
        if (validFiles.length > 0) enqueueFiles(targetCandidateId, validFiles);
    };

    // 受け付けないときもドラッグの既定の動作は止める (止めないとブラウザがファイルを開く。§4.1 ②)。
    // ドラッグ中の見た目 (job-upload-drag-active) は「受け付けられる」ように見えるため出さない
    const handleDragOver = (e) => {
        e.preventDefault();
        if (!canImport) return;
        setIsDragActive(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragActive(false);
        // 残量が足りないときは、形式・サイズの検査 (§5.1 No.1・No.2) も列への追加もせずに捨てる (§4.1 ②)
        if (!canImport) return;
        handleFiles(e.dataTransfer.files);
    };

    // 残量が足りないときはファイルの選択を開かない (§4.1 ①。クリック・Enter・Space の共通処理)
    const openFileDialog = () => {
        if (!canImport) return;
        fileInputRef.current?.click();
    };

    const handleFileInputChange = (e) => {
        handleFiles(e.target.files);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const statusLabel = {
        waiting: '待機中',
        uploading: 'アップロード中...',
        extracting: '抽出中...',
        complete: '完了',
        duplicate: '完了（既存求人を更新・重複の可能性）',
        error: 'エラー',
    };

    return (
        <div className="job-upload-zone">
            <div
                className={`job-upload-dropzone ${isDragActive ? 'job-upload-drag-active' : ''} ${zoneError ? 'job-upload-dropzone-error' : ''} ${canImport ? '' : 'job-upload-dropzone-disabled'}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={openFileDialog}
                role="button"
                tabIndex={0}
                aria-label="求人ファイル (PDF) をアップロード"
                // role="button" の要素のため disabled を付けられない。Tab では移れるまま
                // 操作だけを受け付けない (§7.2「AI ポイントの不足で押せない操作」)
                aria-disabled={canImport ? undefined : 'true'}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openFileDialog();
                    }
                }}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="application/pdf,.pdf"
                    className="job-upload-hidden-input"
                    onChange={handleFileInputChange}
                />
                <span className="job-upload-dropzone-main-text">
                    クリックまたは求人ファイルをドラッグ＆ドロップ
                </span>
                <span className="job-upload-dropzone-sub-text">
                    対応形式: PDF（最大 {MAX_FILE_SIZE_MB}MB）。アップロードされた求人は求人プールに追加され、次回以降も AI マッチング対象になります
                </span>
            </div>

            {/* §5.2 No.5: 形式・サイズの文言 (§5.1 No.1・No.2) があるときはその上に出す (Issue #882) */}
            {!canImport && (
                <p className="job-upload-zone-error-message" role="alert">
                    {INSUFFICIENT_POINTS_MESSAGE}
                </p>
            )}

            {zoneError && (
                <p className="job-upload-zone-error-message" role="alert">
                    {zoneError}
                </p>
            )}

            {entries.length > 0 && (
                <ul className="job-upload-entry-list" aria-live="polite">
                    {entries.map((entry) => (
                        <li key={entry.id} className={`job-upload-entry job-upload-entry-${entry.status}`}>
                            <span className="job-upload-entry-name">{entry.fileName}</span>
                            <span className="job-upload-entry-status">{statusLabel[entry.status]}</span>
                            {entry.status === 'error' && entry.errorMessage && (
                                <span className="job-upload-entry-error" role="alert">{entry.errorMessage}</span>
                            )}
                            {entry.status === 'error' && (
                                <span className="job-upload-entry-actions">
                                    {/* BTN-M-008: 再試行できる失敗のときだけ出す (SCR-M-001 §6.3)。
                                        表示していても、残量が取込に必要なポイントより少ないときは押せない
                                        (再試行も取込確定まで進みポイントを使うため。§4.3、Issue #882) */}
                                    {entry.retryable && (
                                        <button
                                            type="button"
                                            className="modal-button-cancel job-upload-entry-retry"
                                            onClick={() => retryEntry(entry.id)}
                                            disabled={!canImport}
                                            aria-label={`「${entry.fileName}」の取込を再試行`}
                                        >
                                            再試行
                                        </button>
                                    )}
                                    {/* BTN-M-009: 画面の行を消すだけ。求人プールからの削除ではない */}
                                    <button
                                        type="button"
                                        className="job-upload-entry-dismiss"
                                        onClick={() => dismissEntry(entry.id)}
                                        aria-label={`「${entry.fileName}」のエラー表示を閉じる`}
                                    >
                                        閉じる
                                    </button>
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default JobUploadZone;
